import { useState } from 'react';
import { AlertTriangle, ShieldCheck, ShieldAlert, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { checkTosCompliance, TosViolation } from '@/lib/platformFees.js';

interface TOSComplianceGuardProps {
  text: string;
  platform?: string | null;
  onCleanText?: (cleanedText: string) => void;
  className?: string;
  compact?: boolean;
}

export function TOSComplianceGuard({
  text,
  platform,
  onCleanText,
  className = '',
  compact = false,
}: TOSComplianceGuardProps) {
  const [cleaned, setCleaned] = useState(false);
  const result = checkTosCompliance(text, platform);

  if (result.platform !== 'fiverr' && result.platform !== 'upwork') {
    return null;
  }

  if (result.isCompliant) {
    if (compact) return null;
    return (
      <div className={`flex items-center gap-2 p-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs ${className}`}>
        <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
        <span>
          <strong className="font-semibold">{result.platform.toUpperCase()} TOS Protected:</strong> No off-platform contact leaks detected.
        </span>
      </div>
    );
  }

  const handleClean = () => {
    if (onCleanText) {
      onCleanText(result.sanitizedText);
      setCleaned(true);
      setTimeout(() => setCleaned(false), 3000);
    }
  };

  const isCritical = result.riskLevel === 'critical';
  const platformLabel = result.platform === 'fiverr' ? 'Fiverr' : 'Upwork';

  return (
    <div
      className={`rounded-xl border ${
        isCritical
          ? 'border-rose-500/50 bg-rose-950/40 text-rose-200'
          : 'border-amber-500/50 bg-amber-950/40 text-amber-200'
      } p-3 sm:p-4 space-y-3 transition-all animate-in fade-in duration-200 ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldAlert className={`w-5 h-5 ${isCritical ? 'text-rose-400' : 'text-amber-400'} shrink-0 animate-bounce`} />
          <h4 className="font-bold text-sm text-white">
            {platformLabel} TOS Compliance Alert ({result.violations.length} Violation{result.violations.length > 1 ? 's' : ''})
          </h4>
        </div>

        {onCleanText && (
          <Button
            type="button"
            size="sm"
            onClick={handleClean}
            variant="outline"
            className="h-7 text-xs bg-black/40 hover:bg-black/60 border-amber-500/40 text-amber-300 gap-1"
          >
            {cleaned ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Sparkles className="w-3.5 h-3.5" />}
            {cleaned ? 'Redacted!' : 'Auto-Redact Leaks'}
          </Button>
        )}
      </div>

      <p className="text-xs leading-relaxed text-slate-300">
        Sharing external contact info or discussing off-platform payment on <strong className="text-white">{platformLabel}</strong> violates their Terms of Service and risks immediate account shadowban or termination.
      </p>

      <div className="space-y-1.5 pt-1">
        {result.violations.map((v: TosViolation, idx: number) => (
          <div
            key={idx}
            className="flex items-start gap-2 text-xs bg-black/30 p-2 rounded border border-white/5 font-mono"
          >
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <span className="font-bold uppercase tracking-wider text-amber-300 text-[10px] mr-1.5 bg-amber-500/20 px-1.5 py-0.5 rounded">
                {v.type}
              </span>
              <span className="text-slate-200 font-semibold">{v.match}</span>
              <p className="text-[11px] text-slate-400 font-sans mt-0.5">{v.explanation}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
